import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration} from '@azure/msal-angular';
import { AuthenticationResult, InteractionStatus, PopupRequest, RedirectRequest, EventMessage, InteractionType,IdTokenClaims,PromptValue} from '@azure/msal-browser';
import { firstValueFrom, Subject } from 'rxjs';
import { filter, takeUntil, distinctUntilChanged } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { RoleRedirectService } from './shared/service/session/role-redirect.service';
import { CryptoStorageService } from './shared/service/utils/crypto-storage.service';
import { RoleValidatorService } from './core/service/helpers/role-validators.service';
import { LoaderService } from './shared/service/utils/loader.service';
import { NotificationService } from './shared/service/utils/notification.service';
import { LoaderComponent } from './core/components/loader/loader.component';

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
  acr?: string;
  tfp?: string;
  oid?: string;
  sub?: string;
};

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [RouterOutlet, LoaderComponent],
})
export class AppComponent implements OnInit, OnDestroy {
  isIframe = false;
  loginDisplay = false;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private readonly msalGuardConfig: MsalGuardConfiguration,
    private readonly authService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly router: Router,
    private readonly roleRedirectService: RoleRedirectService,
    private readonly crypto: CryptoStorageService,
    private readonly roleValidatorService: RoleValidatorService,
    private readonly loaderService: LoaderService,
    private readonly notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.isIframe = globalThis.window !== globalThis.parent && !globalThis.opener;
    this.authService.handleRedirectObservable().subscribe();
    this.setLoginDisplay();
    this.authService.instance.enableAccountStorageEvents();
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === 'msal:accountAdded' ||
            msg.eventType === 'msal:accountRemoved' ||
            msg.eventType === 'msal:handleRedirectEnd'
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          this.router.navigateByUrl('/').catch(() => this.hardRedirectToRoot());
        } else {
          this.setLoginDisplay();
        }
      });

    // Detectar finalización de interacción (login, popup, redirect)
    this.msalBroadcastService.inProgress$
      .pipe(
        distinctUntilChanged(),
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });

    // Éxito en login, acquireToken o ssoSilent (solo se usa loginSuccess aquí)
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) =>
          ['msal:loginSuccess'].includes(msg.eventType)
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        const idtoken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;
        const tokenAny = idtoken as any;

        const toLc = (s?: string) => s?.toLowerCase();
        const names = environment.b2cPolicies.names;
        const policy = toLc(idtoken?.acr || idtoken?.tfp || '');
        const susi = toLc(names.signUpSignIn);
        const edit = toLc(names.editProfile);
        const reset = toLc(names.resetPassword);
        if (policy === susi) {
          this.authService.instance.setActiveAccount(payload.account);
          const account = this.authService.instance.getActiveAccount();

          if (account?.homeAccountId === payload.account?.homeAccountId) {

            const alreadyStored = localStorage.getItem('userClaims');

            if (alreadyStored) {
              console.warn('Claims ya estaban guardados, no se vuelven a setear.');
            } else {

              const claimsToStore = {
                name: tokenAny.name || '',
                givenName: tokenAny.given_name || '',
                familyName: tokenAny.family_name || '',
                email: tokenAny.email || '',
                group: tokenAny.group || '',
                companyName: tokenAny.companyName || '',
                ruc: tokenAny.ruc || '',
                socialName: tokenAny.socialName || '',
                flowProduce: tokenAny.flowProduce || '',
                flowDigital: tokenAny.flowDigital || ''
              };

              this.crypto.setItemEncrypted('userClaims', claimsToStore);

            }


            (async () => {
              const isLoggedIn = JSON.parse(localStorage.getItem('isLoggedIn') || 'false');
              if (!isLoggedIn){
                const isValidRole = this.roleValidatorService.validate();

                if (!isValidRole) {
                  this.handleInvalidLogin();
                  return;
                }

                this.roleRedirectService.redirectByRole(idtoken);
              } 
            })();
          }
        } else if (policy === edit) {
          this.authService.logoutRedirect();
        } else if (policy === reset) {
          this.login({
            authority: environment.b2cPolicies.authorities.signUpSignIn.authority,
            scopes: ['openid', 'profile'],
            prompt: PromptValue.LOGIN
          } as RedirectRequest);
        }
      });

    // Error de login: redirigir al flujo de "reset password" si aplica
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === 'msal:loginFailure' ||
            msg.eventType === 'msal:acquireTokenFailure'
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        if (result.error?.message?.includes('AADB2C90118')) {
          this.login({
            authority: environment.b2cPolicies.authorities.resetPassword.authority,
            scopes: []
          } as RedirectRequest);
        }
      });
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      const accounts = this.authService.instance.getAllAccounts();
      const toLc = (s?: string) => s?.toLowerCase();
      const susi = toLc(environment.b2cPolicies.names.signUpSignIn);

      const preferred =
        accounts.find((a) => {
          const claims = a.idTokenClaims as IdTokenClaimsWithPolicyId | undefined;
          const accPolicy = toLc(claims?.acr || claims?.tfp || '');
          return accPolicy === susi;
        }) || accounts[0];

      this.authService.instance.setActiveAccount(preferred);
      activeAccount = preferred;
    }

    const idTokenClaims = activeAccount?.idTokenClaims as (IdTokenClaims & { exp?: number }) | undefined;
    if (idTokenClaims?.exp && !environment.production) {
      const expirationTime = idTokenClaims.exp * 1000;
      const expirationDate = new Date(expirationTime);
      const now = Date.now();
      const remainingMs = Math.max(0, expirationTime - now);
      const remainingMinutes = Math.floor(remainingMs / 60000);

      console.log(
        `%c🕒 Token expira el:`,
        'color: orange; font-weight: bold;',
        expirationDate.toLocaleString(), 
        `(${remainingMinutes} min restantes)`
      );
    }

  }

  login(userFlowRequest?: RedirectRequest | PopupRequest) {
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      const req = this.msalGuardConfig.authRequest
        ? { ...(this.msalGuardConfig.authRequest as PopupRequest), ...userFlowRequest }
        : userFlowRequest;

      this.authService.loginPopup(req).subscribe((response: AuthenticationResult) => {
        this.authService.instance.setActiveAccount(response.account);
      });
    } else {
      const req = this.msalGuardConfig.authRequest
        ? { ...(this.msalGuardConfig.authRequest as RedirectRequest), ...userFlowRequest }
        : userFlowRequest;

      this.authService.loginRedirect(req);
    }
  }

  async handleInvalidLogin() {
    localStorage.clear();
    sessionStorage.clear();
    this.notificationService.error('No tienes permisos para acceder al sistema. Se cerrará la sesión.');
    this.loaderService.show('Cerrando sesión');

    try {
      await Promise.race([
        firstValueFrom(this.authService.logoutRedirect()),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('timeout')), 3000)
        )
      ]);

      return; // funcionó MSAL
    } catch (err) {
      console.warn('MSAL logout murió → usando fallback directo:', err);
    }

    this.loaderService.hide();

    const tenantLogout =
      `https://${environment.b2cPolicies.authorityDomain}/` +
      `oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(environment.postLogoutRedirectUri)}`;

    this.hardRedirectToTenantLogout(tenantLogout);
  }

  private hardRedirectToRoot(): void {
    (globalThis.location as any).pathname = '/';
  }

  private hardRedirectToTenantLogout(url: string): void {
    globalThis.location.href = url;
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }
}
